/**
 * Back in Stock / Stock Alert Modal
 * Integrates with Klaviyo Back in Stock API
 */
(function () {
  'use strict';

  const SELECTORS = {
    modal: '#BackInStockModal',
    overlay: '[data-bis-close]',
    sizeButtons: '#bisSizeButtons',
    emailInput: '#bisEmailInput',
    submitBtn: '#bisSubmitBtn',
    form: '#bisForm',
    success: '#bisSuccess',
    error: '#bisError',
    errorMessage: '#bisErrorMessage',
    variantLabel: '#bisVariantLabel',
    dataScript: '#back-in-stock-data',
  };

  let state = {
    selectedVariantId: null,
    selectedVariantTitle: '',
    productData: null,
    klaviyoCompanyId: null,
    isSubmitting: false,
  };

  function init() {
    const dataEl = document.querySelector(SELECTORS.dataScript);
    if (!dataEl) return;

    try {
      const parsed = JSON.parse(dataEl.textContent);
      state.productData = parsed.product;
      state.klaviyoCompanyId = parsed.klaviyoCompanyId;
    } catch (e) {
      console.error('Back in Stock: Failed to parse product data', e);
      return;
    }

    bindCloseHandlers();
    bindSizeButtons();
    bindSubmit();
    bindVariantChangeListener();
  }

  // --- Modal open/close ---

  function openModal(variantId, hideSizePicker, displayTitle) {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    // Reset state
    resetModal();

    // Pre-select variant if provided
    if (variantId) {
      selectVariant(variantId, displayTitle);
    }

    // When opened from a sold-out size click, the user already chose their size —
    // hide the size picker and subtitle, just show email field.
    const sizeButtons = document.querySelector(SELECTORS.sizeButtons);
    const subtitle = document.querySelector('.bis-modal__subtitle');
    if (hideSizePicker && variantId) {
      if (sizeButtons) sizeButtons.style.display = 'none';
      if (subtitle) subtitle.hidden = true;
    }

    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bis-modal-open');

    // Focus the email input after animation
    setTimeout(() => {
      const emailInput = document.querySelector(SELECTORS.emailInput);
      if (emailInput) emailInput.focus();
    }, 300);
  }

  function closeModal() {
    const modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bis-modal-open');
  }

  function resetModal() {
    const form = document.querySelector(SELECTORS.form);
    const success = document.querySelector(SELECTORS.success);
    const error = document.querySelector(SELECTORS.error);
    const emailInput = document.querySelector(SELECTORS.emailInput);
    const submitBtn = document.querySelector(SELECTORS.submitBtn);

    if (form) form.hidden = false;
    if (success) success.hidden = true;
    if (error) error.hidden = true;
    if (emailInput) {
      emailInput.value = '';
      emailInput.classList.remove('is-invalid');
    }
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'NOTIFY ME';
    }
    state.isSubmitting = false;

    // Restore size picker and subtitle visibility
    const sizeButtons = document.querySelector(SELECTORS.sizeButtons);
    const subtitle = document.querySelector('.bis-modal__subtitle');
    if (sizeButtons) sizeButtons.style.display = '';
    if (subtitle) subtitle.hidden = false;

    // Deselect all size buttons
    document.querySelectorAll('.bis-modal__size-btn').forEach((btn) => {
      btn.classList.remove('is-selected');
    });
  }

  function selectVariant(variantId, displayTitle) {
    const btn = document.querySelector(
      `.bis-modal__size-btn[data-variant-id="${variantId}"]`
    );
    if (btn) {
      btn.classList.add('is-selected');
      state.selectedVariantId = variantId;
      state.selectedVariantTitle = displayTitle || btn.dataset.variantTitle;
    } else if (state.productData) {
      // Variant might be in stock — find the first sold-out variant instead
      const firstSoldOut = state.productData.variants.find((v) => !v.available);
      if (firstSoldOut) {
        const fallbackBtn = document.querySelector(
          `.bis-modal__size-btn[data-variant-id="${firstSoldOut.id}"]`
        );
        if (fallbackBtn) {
          fallbackBtn.classList.add('is-selected');
          state.selectedVariantId = firstSoldOut.id;
          state.selectedVariantTitle = displayTitle || fallbackBtn.dataset.variantTitle;
        }
      }
    }

    // Update variant label
    const label = document.querySelector(SELECTORS.variantLabel);
    if (label) {
      label.textContent = state.selectedVariantTitle;
    }
  }

  // --- Event binding ---

  function bindCloseHandlers() {
    // Close on overlay click and close button
    document.querySelectorAll('[data-bis-close]').forEach((el) => {
      el.addEventListener('click', closeModal);
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const modal = document.querySelector(SELECTORS.modal);
        if (modal && modal.getAttribute('aria-hidden') === 'false') {
          closeModal();
        }
      }
    });
  }

  function bindSizeButtons() {
    document.querySelectorAll('.bis-modal__size-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        // Deselect all
        document.querySelectorAll('.bis-modal__size-btn').forEach((b) => {
          b.classList.remove('is-selected');
        });
        // Select this one
        btn.classList.add('is-selected');
        state.selectedVariantId = btn.dataset.variantId;
        state.selectedVariantTitle = btn.dataset.variantTitle;

        const label = document.querySelector(SELECTORS.variantLabel);
        if (label) label.textContent = state.selectedVariantTitle;
      });
    });
  }

  function bindSubmit() {
    const submitBtn = document.querySelector(SELECTORS.submitBtn);
    if (!submitBtn) return;

    submitBtn.addEventListener('click', handleSubmit);

    // Also submit on Enter key in email input
    const emailInput = document.querySelector(SELECTORS.emailInput);
    if (emailInput) {
      emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit();
        }
      });
    }
  }

  function bindVariantChangeListener() {
    // Event delegation on stable <product-info> wrapper, capture phase,
    // so we fire before Dawn's bubble listeners. Survives DOM re-renders
    // that happen when colour swatches are changed.
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    productInfo.addEventListener('click', (e) => {
      const label = e.target.closest('.product-form__input--pill label');
      if (!label) return;

      const input = document.getElementById(label.htmlFor) || label.previousElementSibling;
      if (!input || !input.classList.contains('disabled')) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      // Build display title using active size system tab + visible label text
      const sizeSystem = document.querySelector('.size-tabs li.active')?.textContent?.trim() || '';
      const sizeValue = label.textContent.trim();
      const displayTitle = sizeSystem ? `${sizeSystem}: ${sizeValue}` : sizeValue;

      const variantValue = input.value;
      if (!state.productData) { openModal(null, true, displayTitle); return; }

      const variant = state.productData.variants.find(
        (v) =>
          !v.available &&
          (v.option1 === variantValue ||
            v.option2 === variantValue ||
            v.option3 === variantValue ||
            v.title === variantValue)
      );
      openModal(variant ? variant.id : null, true, displayTitle);
    }, true); // capture=true fires before Dawn's bubble listeners
  }

  // --- Form submission ---

  async function handleSubmit() {
    if (state.isSubmitting) return;

    const emailInput = document.querySelector(SELECTORS.emailInput);
    const email = emailInput ? emailInput.value.trim() : '';

    // Validate email
    if (!email || !isValidEmail(email)) {
      emailInput.classList.add('is-invalid');
      emailInput.focus();
      return;
    }
    emailInput.classList.remove('is-invalid');

    // Validate variant selection
    if (!state.selectedVariantId) {
      showError('Please select a size.');
      return;
    }

    state.isSubmitting = true;
    const submitBtn = document.querySelector(SELECTORS.submitBtn);
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'NOTIFYING...';
    }

    try {
      await submitToKlaviyo(email, state.selectedVariantId);
      showSuccess();
    } catch (err) {
      console.error('Back in Stock: Submission failed', err);
      showError('Something went wrong. Please try again.');
      state.isSubmitting = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'NOTIFY ME';
      }
    }
  }

  // BIS proxy worker — calls Klaviyo Server API server-side. Avoids
  // Safari ITP / Firefox ETP / adblockers blocking a.klaviyo.com directly.
  // Replace BIS_SHARED_SECRET with the value passed to
  // `wrangler secret put BIS_SHARED_SECRET`.
  const BIS_PROXY_URL = 'https://avancus-bis.avancus-faiz.workers.dev/';
  const BIS_SHARED_SECRET = '15ea585bad54890d050783ee7abd2864605b125eb3b76729';

  async function submitToKlaviyo(email, variantId) {
    const response = await fetch(BIS_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-BIS-Token': BIS_SHARED_SECRET,
      },
      body: JSON.stringify({ email, variantId: String(variantId) }),
    });

    if (!response.ok) {
      // Coarse error code only — never echo upstream detail to the user.
      let code = 'unknown';
      try {
        const errBody = await response.json();
        if (errBody && typeof errBody.error === 'string') code = errBody.error;
      } catch (_) {}
      throw new Error(`BIS proxy: ${response.status} ${code}`);
    }
  }

  // --- UI helpers ---

  function showSuccess() {
    const form = document.querySelector(SELECTORS.form);
    const success = document.querySelector(SELECTORS.success);
    const error = document.querySelector(SELECTORS.error);
    const subtitle = document.querySelector('.bis-modal__subtitle');

    if (form) form.hidden = true;
    if (success) success.hidden = false;
    if (error) error.hidden = true;
    if (subtitle) subtitle.hidden = true;

    // Hide size buttons on success
    const sizes = document.querySelector(SELECTORS.sizeButtons);
    if (sizes) sizes.style.display = 'none';
  }

  function showError(message) {
    const error = document.querySelector(SELECTORS.error);
    const errorMessage = document.querySelector(SELECTORS.errorMessage);
    if (error) error.hidden = false;
    if (errorMessage) errorMessage.textContent = message;
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // --- Expose globally ---
  window.openBackInStockModal = openModal;

  // Init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
